import { ArrowRight, ShieldCheck } from "lucide-react";
import { IMAGES } from "@/lib/config";

export default function Hero() {
    const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden"
        >
            {/* subtle texture */}
            <div className="absolute inset-0 grain-bg pointer-events-none" />

            <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center relative">
                <div className="lg:col-span-6 animate-fade-up">
                    <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 bg-sand/60 border border-border rounded-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand" />
                        <span className="label-overline text-ink/80">Artisan · Diagnostiqueur du bâti ancien</span>
                    </div>

                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-ink leading-[1.05]">
                        Diagnostic & restauration
                        <br />
                        <span className="italic font-normal text-brand">du bâti ancien.</span>
                    </h1>

                    <p className="mt-6 text-base md:text-lg text-stone2 max-w-xl leading-relaxed">
                        Soigner les murs centenaires avec des matériaux qui les respectent.
                        Chaux, terre, chanvre, pierre — un savoir-faire transmis,
                        des préconisations claires et un accompagnement de bout en bout.
                    </p>

                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <button
                            onClick={() => scrollTo("contact")}
                            data-testid="hero-cta-devis"
                            className="group inline-flex items-center gap-2 px-7 py-3.5 bg-brand hover:bg-brand-hover text-cream rounded-sm font-medium tracking-tight transition-colors"
                        >
                            Demander un diagnostic
                            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                        </button>
                        <button
                            onClick={() => scrollTo("formations")}
                            data-testid="hero-cta-formations"
                            className="inline-flex items-center gap-2 px-7 py-3.5 border border-border hover:border-brand bg-card text-ink rounded-sm font-medium transition-colors"
                        >
                            Voir les formations
                        </button>
                    </div>

                    <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
                        <Stat label="Années d'expérience" value="10+" />
                        <Stat label="Diplôme universitaire en bâti ancien" value="1" />
                        <Stat label="Stagiaires & apprentis formés" value="~10" />
                    </div>
                </div>

                <div className="lg:col-span-6 relative">
                    <div className="relative aspect-[4/5] lg:aspect-[3/4] overflow-hidden rounded-sm border border-border">
                        <img
                            src={IMAGES.hero}
                            alt="Mur ancien en pierre en cours de restauration avec enduit à la chaux — diagnostic du bâti ancien"
                            loading="eager"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    {/* floating tag */}
                    <div className="hidden md:flex absolute -left-6 bottom-10 bg-cream border border-border rounded-sm px-5 py-4 shadow-md max-w-[260px] gap-3 items-start">
                        <ShieldCheck className="text-brand mt-0.5 shrink-0" size={20} />
                        <div>
                            <div className="font-heading text-sm font-semibold text-ink tracking-tight">
                                Diagnostic indépendant
                            </div>
                            <p className="text-xs text-stone2 mt-1 leading-snug">
                                Un avis libre, des préconisations adaptées à votre maison.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Stat({ value, label }) {
    return (
        <div>
            <div className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-ink">
                {value}
            </div>
            <div className="label-overline text-stone2 mt-1">{label}</div>
        </div>
    );
}
